/**
 * CaptacionOrigenTab — «Captación por origen», de la consulta al quirófano
 * ------------------------------------------------------------------------
 * KPI de dirección (23/9/2026). De los pacientes que vienen a una
 * primera valoración, cuántos acaban en quirófano, repartidos por «cómo nos
 * conoció». Vive en el Dashboard junto a «KPIs Dirección» y con la misma
 * puerta: solo admin y dirección (el padre la esconde al resto y el servidor,
 * `require_management`, les contesta 403).
 *
 * Lo primero que tiene que enseñar es el AGUJERO: las consultas sin origen.
 * Por eso «Sin origen» va aparte y nunca repartido, y el chip de «% sin
 * origen» se pone en rojo pasado el umbral.
 *
 * El filtro por procedimiento ofrece los de la base (las plantillas de
 * consentimiento a las que apunta el catálogo de procedimientos): ninguno
 * escrito aquí.
 *
 * Si la lectura falla se dice: una tabla de ceros se leería como «este
 * semestre no ha venido nadie por Instagram».
 *
 * Consume:
 *   GET /api/indicadores/origen-captacion?from&to&procedimiento
 *   GET /api/indicadores/origen-captacion/procedimientos
 *   GET/PUT /api/indicadores/origen-captacion/catalogo
 */
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api, formatDate, mensajeError } from '../lib/api';
import { MiniStat, SectionCard } from './Common';
import { Button } from './ui/button';
import {
  Users, Scissors, TrendingUp, Loader2, ChevronDown, ChevronRight, Plus, Trash2, Info,
  AlertTriangle, RotateCcw,
} from 'lucide-react';

// Por encima de este % de pacientes «Sin origen», el reparto por canales no
// es de fiar: cada paciente sin origen es uno que le falta a algún canal, así
// que todos salen por debajo de lo real. Dirección fijó el 20 % (23/9/2026);
// cuando el campo sea obligatorio (Fase 2) debería bajar solo.
export const UMBRAL_SIN_ORIGEN_PCT = 20;

export const URL_CAPTACION = '/api/indicadores/origen-captacion';
export const URL_CATALOGO_ORIGENES = '/api/indicadores/origen-captacion/catalogo';
export const URL_PROCEDIMIENTOS_CAPTACION = '/api/indicadores/origen-captacion/procedimientos';

const PRESETS = [
  { id: '3m', texto: '3 meses' },
  { id: '6m', texto: '6 meses' },
  { id: 'anio', texto: 'Año en curso' },
  { id: 'mes', texto: 'Mes actual' },
];

// Fecha LOCAL en YYYY-MM-DD: `toISOString` es UTC y pasada la medianoche en
// España daría el día anterior.
function ymd(d) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// El mismo día `meses` atrás; si ese mes es más corto, su último día (igual
// que el servidor: 31 de agosto − 6 meses = 28 de febrero).
function restarMeses(d, meses) {
  const total = d.getFullYear() * 12 + d.getMonth() - meses;
  const anio = Math.floor(total / 12);
  const mes = total % 12;
  const ultimo = new Date(anio, mes + 1, 0).getDate();
  return new Date(anio, mes, Math.min(d.getDate(), ultimo));
}

export function ventanaDePreset(preset, hoy = new Date()) {
  switch (preset) {
    case '3m': return { desde: ymd(restarMeses(hoy, 3)), hasta: ymd(hoy) };
    case 'anio': return { desde: `${hoy.getFullYear()}-01-01`, hasta: ymd(hoy) };
    case 'mes': return { desde: ymd(new Date(hoy.getFullYear(), hoy.getMonth(), 1)), hasta: ymd(hoy) };
    case '6m':
    default: return { desde: ymd(restarMeses(hoy, 6)), hasta: ymd(hoy) };
  }
}

// Sin consultas no hay conversión: «—», no un 0 % que se lee como dato.
const fmtPct = (v) => (v === null || v === undefined
  ? '—'
  : `${Number(v).toLocaleString('es-ES', { maximumFractionDigits: 1 })} %`);

const slug = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

// Sinónimos UNO POR LÍNEA, no separados por comas (pasada final del
// 23/9/2026): «Me lo dijo Ana, la vecina» añadido desde «sin clasificar» se
// partía en dos sinónimos que no casaban con nada.
const aLineas = (lista) => (lista || []).join('\n');
const deLineas = (texto) => String(texto || '').split('\n').map((s) => s.trim()).filter(Boolean);

// `original` es el nombre con el que se leyó la fila: si se cambia, el
// servidor guarda el viejo como sinónimo del nuevo (va como `nombre_anterior`)
// y las fichas que lo decían no se van a «Otros».
let siguienteClave = 0;
const aBorrador = (origenes) => (origenes || []).map((o) => {
  siguienteClave += 1;
  return {
    clave: `o${siguienteClave}`, origen: o.origen, original: o.origen || null,
    sinonimos: aLineas(o.sinonimos),
  };
});

// La MISMA normalización que el servidor (quirofano_matcher._normalize): sin
// acentos ni mayúsculas, lo que no es letra o número pasa a espacio.
export const normalizar = (s) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();

// ¿Vale como sinónimo? (última pasada del 23/9/2026). Las reglas las manda el
// servidor en el catálogo (`reglas_sinonimo`): una sola lista, la suya; sin
// ellas no se adivina y el servidor lo dirá al guardar.
export function motivoSinonimo(texto, reglas) {
  const escrito = String(texto ?? '').trim();
  const k = normalizar(escrito);
  if (!k || !reglas) return '';
  if ((reglas.abreviaturas || []).includes(k)) return '';
  const minimo = reglas.min_letras || 3;
  if (k.replace(/ /g, '').length < minimo) {
    return `«${escrito}» es demasiado corto para ser sinónimo (al menos ${minimo} letras).`;
  }
  if ((reglas.palabras_vacias || []).includes(k)) {
    return `«${escrito}» es demasiado genérico para ser sinónimo: casaría con fichas de cualquier origen.`;
  }
  return '';
}

const fichasTexto = (n) => (n === 1 ? '1 ficha' : `${n} fichas`);

function FilaOrigen({ fila, aparte = false }) {
  const tono = fila.tipo === 'sin_origen'
    ? 'bg-red-50/60 dark:bg-red-950/30'
    : aparte ? 'bg-[rgba(222,222,222,0.25)]' : '';
  return (
    <tr className={`border-t border-border ${tono}`} data-testid={`captacion-fila-${slug(fila.origen)}`}>
      <td className="py-2 px-2">
        {fila.origen}
        {fila.tipo === 'sin_origen' && (
          <span className="ml-2 text-[11px] text-muted-foreground">(ficha y LOPD vacías)</span>
        )}
        {fila.tipo === 'no_lo_recuerda' && (
          <span className="ml-2 text-[11px] text-muted-foreground">(respuesta, no canal)</span>
        )}
      </td>
      <td className="py-2 px-2 text-right tabular-nums">{fila.consultas}</td>
      <td className="py-2 px-2 text-right tabular-nums">{fila.qx}</td>
      <td className="py-2 px-2 text-right tabular-nums font-semibold text-[#13231E]">{fmtPct(fila.pct)}</td>
    </tr>
  );
}

export default function CaptacionOrigenTab() {
  const inicial = ventanaDePreset('6m');
  const [preset, setPreset] = useState('6m');
  const [desde, setDesde] = useState(inicial.desde);
  const [hasta, setHasta] = useState(inicial.hasta);
  const [procedimiento, setProcedimiento] = useState('');
  const [procedimientos, setProcedimientos] = useState([]);
  const [recarga, setRecarga] = useState(0);

  const [datos, setDatos] = useState(null);
  const [fallo, setFallo] = useState('');
  const [cargando, setCargando] = useState(false);

  const [catalogo, setCatalogo] = useState(null);
  const [falloCatalogo, setFalloCatalogo] = useState('');
  const [borrador, setBorrador] = useState([]);
  // Los sinónimos de la fila reservada «No lo recuerda» (una por línea).
  const [noLoRecuerda, setNoLoRecuerda] = useState('');
  const [editorAbierto, setEditorAbierto] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [errorGuardar, setErrorGuardar] = useState('');
  const [destinoSinonimo, setDestinoSinonimo] = useState({});
  // Orígenes quitados que tenían fichas y a qué fila pasan (última pasada
  // del 23/9/2026): [{ origen, destinoClave, fichas }].
  const [pasos, setPasos] = useState([]);
  const [quitando, setQuitando] = useState(null);
  const [destinoQuitar, setDestinoQuitar] = useState('');
  // Volver a los de fábrica: la lista de lo propio que se pierde, a confirmar.
  const [restaurando, setRestaurando] = useState(null);
  const [avisosGuardado, setAvisosGuardado] = useState([]);
  const editorRef = useRef(null);

  useEffect(() => {
    // Una fecha a medio escribir llega vacía: no se pide nada (el servidor
    // pondría su ventana por defecto y la pantalla diría otra).
    if (!desde || !hasta) return undefined;
    let off = false;
    setCargando(true);
    setFallo('');
    const params = { from: desde, to: hasta };
    if (procedimiento) params.procedimiento = procedimiento;
    api.get(URL_CAPTACION, { params })
      .then((r) => { if (!off) setDatos(r.data); })
      .catch((e) => {
        if (!off) { setDatos(null); setFallo(mensajeError(e, 'no se ha podido consultar')); }
      })
      .finally(() => { if (!off) setCargando(false); });
    return () => { off = true; };
  }, [desde, hasta, procedimiento, recarga]);

  // Los procedimientos del selector, de la base. Si la lectura falla, el
  // selector se queda con «todas las primeras valoraciones».
  useEffect(() => {
    let off = false;
    api.get(URL_PROCEDIMIENTOS_CAPTACION)
      .then((r) => { if (!off) setProcedimientos(Array.isArray(r.data?.procedimientos) ? r.data.procedimientos : []); })
      .catch(() => { if (!off) setProcedimientos([]); });
    return () => { off = true; };
  }, []);

  useEffect(() => {
    let off = false;
    api.get(URL_CATALOGO_ORIGENES)
      .then((r) => {
        if (off) return;
        setCatalogo(r.data);
        setBorrador(aBorrador(r.data?.origenes));
        setNoLoRecuerda(aLineas(r.data?.sinonimos_no_lo_recuerda));
        setFalloCatalogo('');
      })
      .catch((e) => { if (!off) setFalloCatalogo(mensajeError(e, 'no se ha podido leer el catálogo')); });
    return () => { off = true; };
  }, []);

  const elegirPreset = (id) => {
    const v = ventanaDePreset(id);
    setPreset(id);
    setDesde(v.desde);
    setHasta(v.hasta);
  };

  const cambiarOrigen = (clave, campo, valor) => {
    setBorrador((b) => b.map((o) => (o.clave === clave ? { ...o, [campo]: valor } : o)));
  };

  const anadirComoSinonimo = (texto) => {
    const destino = destinoSinonimo[texto] || '';
    if (!destino) return;
    setBorrador((b) => b.map((o) => {
      if (o.origen !== destino) return o;
      return { ...o, sinonimos: aLineas([...deLineas(o.sinonimos), texto]) };
    }));
    setEditorAbierto(true);
    toast.info(`«${texto}» añadido a ${destino}. Pulsa «Guardar catálogo» para aplicarlo.`);
    setTimeout(() => editorRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }), 0);
  };

  const fichasDe = (nombre) => Number((catalogo?.fichas_por_origen || {})[nombre] || 0);
  const nombreDeClave = (clave) => borrador.find((o) => o.clave === clave)?.origen?.trim() || '';

  // Quitar un origen con fichas: se dice cuántas y hay que elegir a dónde
  // pasan; el servidor guarda allí su nombre (y sus sinónimos). Sin fichas,
  // o una fila nueva, se quita sin más.
  const pedirQuitar = (o) => {
    if (o.original && fichasDe(o.original) > 0) {
      setQuitando(o.clave);
      setDestinoQuitar('');
    } else {
      setBorrador((b) => b.filter((x) => x.clave !== o.clave));
    }
  };
  const confirmarQuitar = () => {
    const o = borrador.find((x) => x.clave === quitando);
    if (!o || !destinoQuitar) return;
    setPasos((p) => [...p.filter((x) => x.origen !== o.original),
      { origen: o.original, destinoClave: destinoQuitar, fichas: fichasDe(o.original) }]);
    setBorrador((b) => b.filter((x) => x.clave !== quitando));
    setQuitando(null);
  };

  // Volver a los de fábrica: primero, qué orígenes propios se pierden y
  // cuántas fichas tienen; al confirmar, sus nombres quedan en «Otros», a la
  // vista (salvo los que ya casan con uno de fábrica o no valen de sinónimo).
  const origenesPropios = () => {
    const fabrica = new Set((catalogo?.valores_por_defecto || []).map((o) => normalizar(o.origen)));
    return borrador.filter((o) => o.original && !fabrica.has(normalizar(o.original)))
      .map((o) => ({ nombre: o.original, fichas: fichasDe(o.original) }));
  };
  const aplicarRestaurar = (propios) => {
    const otros = catalogo?.otros || 'Otros';
    const fabrica = catalogo?.valores_por_defecto || [];
    const casan = new Set(fabrica.flatMap((o) => [o.origen, ...(o.sinonimos || [])]).map(normalizar));
    const aOtros = propios.map((p) => p.nombre)
      .filter((n) => !casan.has(normalizar(n)) && !motivoSinonimo(n, catalogo?.reglas_sinonimo));
    const filas = aBorrador(fabrica).map((o) => (o.origen === otros
      ? { ...o, sinonimos: aLineas([...deLineas(o.sinonimos), ...aOtros]) } : o));
    const claveOtros = filas.find((o) => o.origen === otros)?.clave;
    setBorrador(filas);
    setNoLoRecuerda(aLineas(catalogo?.valores_por_defecto_no_lo_recuerda));
    setPasos(propios.filter((p) => !casan.has(normalizar(p.nombre)))
      .map((p) => ({ origen: p.nombre, destinoClave: claveOtros, fichas: p.fichas })));
    setQuitando(null);
    setRestaurando(null);
  };
  const pedirRestaurar = () => {
    const propios = origenesPropios();
    if (propios.length) setRestaurando(propios);
    else aplicarRestaurar([]);
  };

  const guardarCatalogo = () => {
    // Un destino que ya no está (se quitó después): no se envía nada.
    if (pasos.some((p) => !nombreDeClave(p.destinoClave))) return;
    setGuardando(true);
    setErrorGuardar('');
    setAvisosGuardado([]);
    const origenes = borrador.map((o) => ({
      origen: o.origen,
      sinonimos: deLineas(o.sinonimos),
      nombre_anterior: o.original,
    }));
    const cuerpo = { origenes, sinonimos_no_lo_recuerda: deLineas(noLoRecuerda) };
    if (pasos.length) {
      cuerpo.borrados = pasos.map((p) => ({ origen: p.origen, pasa_a: nombreDeClave(p.destinoClave) }));
    }
    api.put(URL_CATALOGO_ORIGENES, cuerpo)
      .then((r) => {
        setCatalogo(r.data);
        setBorrador(aBorrador(r.data?.origenes));
        setNoLoRecuerda(aLineas(r.data?.sinonimos_no_lo_recuerda));
        setPasos([]);
        setAvisosGuardado(r.data?.avisos || []);
        toast.success('Catálogo de orígenes guardado');
        setRecarga((n) => n + 1);
      })
      .catch((e) => setErrorGuardar(mensajeError(e, 'No se ha podido guardar el catálogo')))
      .finally(() => setGuardando(false));
  };

  const totales = datos?.totales || {};
  const canales = (datos?.filas || []).filter((f) => f.tipo === 'canal');
  const aparte = (datos?.filas || []).filter((f) => f.tipo !== 'canal');
  const pctSin = totales.pct_sin_origen;
  // Con el RECUENTO, no con el porcentaje redondeado (pasada final del
  // 23/9/2026): 1001 de 5000 es un 20,02 %, sale «20 %» y sí pasa del umbral.
  const sinOrigenAlto = (totales.sin_origen !== undefined && totales.sin_origen !== null && totales.consultas)
    ? totales.sin_origen * 100 > UMBRAL_SIN_ORIGEN_PCT * totales.consultas
    : pctSin !== null && pctSin !== undefined && pctSin > UMBRAL_SIN_ORIGEN_PCT;
  const nombresCatalogo = borrador.map((o) => o.origen).filter((n) => n.trim());
  const reglas = catalogo?.reglas_sinonimo;
  const nombreOtros = catalogo?.otros || 'Otros';

  // El nombre de cada fila, validado AL ESCRIBIR (última pasada del
  // 23/9/2026): «Otros» en una fila nueva dejaba el editor atascado. El
  // campo sigue editable siempre (lo que se bloquea es la fila de «Otros» que
  // vino del servidor) y guardar espera a que no haya errores.
  const reservados = new Set([normalizar(catalogo?.sin_origen || 'Sin origen'),
    normalizar(catalogo?.no_lo_recuerda || 'No lo recuerda')]);
  const erroresNombre = {};
  borrador.forEach((o) => {
    const k = normalizar(o.origen);
    const repetido = borrador.find((x) => x.clave !== o.clave && normalizar(x.origen) === k);
    if (!k) erroresNombre[o.clave] = 'Ponle un nombre (o quita la fila).';
    else if (k === normalizar(nombreOtros) && o.original !== nombreOtros) {
      erroresNombre[o.clave] = `«${nombreOtros}» ya existe: es donde va lo que no casa. Elige otro nombre.`;
    } else if (reservados.has(k)) erroresNombre[o.clave] = `«${o.origen.trim()}» se cuenta aparte: no puede ser un origen.`;
    else if (repetido) erroresNombre[o.clave] = `Repetido: ya hay un origen «${repetido.origen.trim()}».`;
  });
  const destinoPerdido = pasos.some((p) => !nombreDeClave(p.destinoClave));
  const hayErrores = Object.keys(erroresNombre).length > 0 || destinoPerdido;

  // Qué pasará con el nombre viejo de una fila renombrada: nada de prometer
  // lo que el servidor no va a hacer.
  const avisoRenombrado = (o) => {
    const k = normalizar(o.original);
    const dueno = borrador.find((x) => x.clave !== o.clave
      && [x.origen, ...deLineas(x.sinonimos)].some((t) => normalizar(t) === k));
    if (dueno) {
      return `«${o.original}» ya lleva a «${dueno.origen.trim()}»: no se guardará como sinónimo de «${o.origen.trim()}».`;
    }
    const motivo = motivoSinonimo(o.original, reglas);
    if (motivo) return `${motivo} No se guardará como sinónimo: sus fichas irán a «${nombreOtros}».`;
    return `«${o.original}» se guardará como sinónimo, para que sus fichas no se vayan a «${nombreOtros}».`;
  };

  return (
    <div data-testid="captacion-origen-tab">
      {/* Ventana y procedimiento */}
      <div className="cp-card p-4 mb-5 flex flex-wrap items-center gap-3">
        <div className="text-xs uppercase tracking-wider font-semibold text-[#13231E]">Ventana</div>
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Periodo">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => elegirPreset(p.id)}
              aria-pressed={preset === p.id}
              data-testid={`captacion-preset-${p.id}`}
              className={`px-2.5 py-1 rounded-md border text-sm transition-colors ${
                preset === p.id
                  ? 'bg-[#2E6E5E] text-white border-[#2E6E5E]'
                  : 'border-border bg-card text-[#13231E] hover:border-[#2E6E5E]/50'}`}
            >
              {p.texto}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-1.5 text-sm">
          <span className="text-muted-foreground">Desde</span>
          <input
            type="date"
            value={desde}
            max={hasta || undefined}
            onChange={(e) => { setPreset(''); setDesde(e.target.value); }}
            className="px-2 py-1 rounded-md border border-border bg-card text-sm font-mono text-[#13231E]"
            data-testid="captacion-desde"
          />
        </label>
        <label className="flex items-center gap-1.5 text-sm">
          <span className="text-muted-foreground">Hasta</span>
          <input
            type="date"
            value={hasta}
            min={desde || undefined}
            onChange={(e) => { setPreset(''); setHasta(e.target.value); }}
            className="px-2 py-1 rounded-md border border-border bg-card text-sm font-mono text-[#13231E]"
            data-testid="captacion-hasta"
          />
        </label>
        <label className="flex items-center gap-1.5 text-sm">
          <span className="text-muted-foreground">Procedimiento</span>
          <select
            value={procedimiento}
            onChange={(e) => setProcedimiento(e.target.value)}
            className="px-2 py-1 rounded-md border border-border bg-card text-sm text-[#13231E]"
            data-testid="captacion-procedimiento"
          >
            <option value="">Todas las primeras valoraciones</option>
            {procedimientos.map((p) => (
              <option key={p.code} value={p.code}>{p.nombre}</option>
            ))}
          </select>
        </label>
        {cargando && <Loader2 className="w-4 h-4 animate-spin text-[#2E6E5E]" aria-label="Cargando" />}
      </div>

      {fallo ? (
        <div className="cp-card p-4 mb-5 border-red-300 bg-red-50/60 dark:bg-red-950/30" role="alert" data-testid="captacion-fallo">
          <p className="text-sm text-red-700 dark:text-red-300">
            No se ha podido leer la captación por origen: {fallo}. Esto no es un cero: no hay datos
            que enseñar hasta que se pueda leer.
          </p>
          <Button variant="outline" size="sm" className="mt-2" onClick={() => setRecarga((n) => n + 1)}>
            <RotateCcw className="w-4 h-4 mr-1.5" /> Reintentar
          </Button>
        </div>
      ) : !datos ? (
        <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>
      ) : (
        <>
          <div className="text-xs text-muted-foreground mb-2" data-testid="captacion-ventana">
            Del {formatDate(datos.ventana?.desde)} al {formatDate(datos.ventana?.hasta)}
            {datos.procedimiento
              ? ` · solo ${datos.procedimiento_nombre || datos.procedimiento}`
              : ' · todas las primeras valoraciones'}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-3" data-testid="captacion-totales">
            <MiniStat label="Pacientes con consulta" value={totales.consultas ?? '—'}
              hint="primera valoración · 1 por paciente" icon={Users} />
            <MiniStat label="Operados" value={totales.qx ?? '—'}
              hint="cirugía igual o posterior a la consulta" icon={Scissors} />
            <MiniStat label="Conversión" value={fmtPct(totales.pct)}
              hint="operados / pacientes con consulta" icon={TrendingUp} />
          </div>

          <div className="flex flex-wrap items-center gap-3 mb-5">
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-sm font-medium ${
                sinOrigenAlto
                  ? 'bg-red-100 text-red-900 border-red-300 dark:bg-red-950/50 dark:text-red-200 dark:border-red-800'
                  : 'bg-[rgba(46,110,94,0.10)] text-[#13231E] border-[rgba(46,110,94,0.28)]'}`}
              data-testid="captacion-chip-sin-origen"
              data-alerta={sinOrigenAlto ? 'true' : 'false'}
            >
              {sinOrigenAlto && <AlertTriangle className="w-4 h-4" />}
              {/* En el borde (20,02 % redondea a «20 %») se dice «más del»:
                  el rojo y la cifra no pueden contradecirse. */}
              {sinOrigenAlto && pctSin !== null && pctSin !== undefined && pctSin <= UMBRAL_SIN_ORIGEN_PCT
                ? `más del ${UMBRAL_SIN_ORIGEN_PCT} % sin origen`
                : `${fmtPct(pctSin)} sin origen`}
            </span>
            {sinOrigenAlto && (
              <span className="text-sm text-red-700 dark:text-red-300" data-testid="captacion-aviso-sin-origen">
                Mientras el % sin origen sea alto, el resto de canales está infraestimado.
              </span>
            )}
          </div>

          {datos.citas_sin_paciente > 0 && (
            <p className="text-xs text-muted-foreground mb-4" data-testid="captacion-sin-paciente">
              {datos.citas_sin_paciente === 1
                ? 'Hay 1 cita de primera valoración sin paciente enlazado: no cuenta (no hay ficha de la que sacar el origen).'
                : `Hay ${datos.citas_sin_paciente} citas de primera valoración sin paciente enlazado: no cuentan (no hay ficha de la que sacar el origen).`}
            </p>
          )}
          {datos.citas_fichas_borradas > 0 && (
            <p className="text-xs text-muted-foreground mb-4" data-testid="captacion-fichas-borradas">
              {datos.citas_fichas_borradas === 1
                ? 'Hay 1 cita de fichas borradas: no cuenta (la ficha ya no existe; no es «Sin origen»).'
                : `Hay ${datos.citas_fichas_borradas} citas de fichas borradas: no cuentan (la ficha ya no existe; no es «Sin origen»).`}
            </p>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <SectionCard title="Por origen" className="lg:col-span-3">
              {!totales.consultas ? (
                <div className="py-10 text-center text-sm text-muted-foreground" data-testid="captacion-vacio">
                  No hay primeras valoraciones en esta ventana.
                </div>
              ) : (
                <div className="overflow-x-auto" data-testid="captacion-tabla">
                  <table className="w-full text-sm">
                    <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                      <tr>
                        <th className="py-2 px-2">Origen</th>
                        <th className="py-2 px-2 text-right">Consultas</th>
                        <th className="py-2 px-2 text-right">QX</th>
                        <th className="py-2 px-2 text-right">Conversión</th>
                      </tr>
                    </thead>
                    <tbody>
                      {canales.map((f) => <FilaOrigen key={f.origen} fila={f} />)}
                      <tr>
                        <td colSpan={4} className="pt-4 pb-1 px-2 text-[11px] uppercase tracking-wider text-muted-foreground">
                          Aparte (no se reparte entre los canales)
                        </td>
                      </tr>
                      {aparte.map((f) => <FilaOrigen key={f.origen} fila={f} aparte />)}
                    </tbody>
                  </table>
                </div>
              )}
            </SectionCard>

            <div className="lg:col-span-2 space-y-6">
              <div className="cp-card p-4 bg-[rgba(46,110,94,0.06)]" data-testid="captacion-regla">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-[#13231E] mb-2">
                  <Info className="w-4 h-4" /> Cómo se cuenta
                </div>
                <p className="text-sm text-[#13231E] leading-relaxed">{datos.regla}</p>
              </div>

              {(datos.sin_clasificar || []).length > 0 && (
                <SectionCard title="Textos sin clasificar (van a «Otros»)">
                  <p className="text-xs text-muted-foreground mb-2">
                    Lo que está escrito en la ficha pero no casa con ningún origen. Añádelo como
                    sinónimo y dejará de caer en «Otros».
                  </p>
                  <ul className="divide-y divide-border" data-testid="captacion-sin-clasificar">
                    {datos.sin_clasificar.map((s) => (
                      <li key={s.texto} className="py-2 flex flex-wrap items-center gap-2 text-sm">
                        <span className="font-medium text-[#13231E]">«{s.texto}»</span>
                        <span className="text-muted-foreground tabular-nums">
                          {s.pacientes} {s.pacientes === 1 ? 'paciente' : 'pacientes'}
                        </span>
                        {motivoSinonimo(s.texto, reglas) ? (
                          <span className="ml-auto text-xs text-muted-foreground" data-testid="captacion-sinonimo-no-vale">
                            {motivoSinonimo(s.texto, reglas)}
                          </span>
                        ) : nombresCatalogo.length > 0 && (
                          <span className="ml-auto flex items-center gap-1.5">
                            <select
                              value={destinoSinonimo[s.texto] || ''}
                              onChange={(e) => setDestinoSinonimo((d) => ({ ...d, [s.texto]: e.target.value }))}
                              className="px-1.5 py-0.5 rounded border border-border bg-card text-xs"
                              aria-label={`Origen para «${s.texto}»`}
                            >
                              <option value="">Añadir a…</option>
                              {nombresCatalogo.map((n) => <option key={n} value={n}>{n}</option>)}
                            </select>
                            <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs"
                              disabled={!destinoSinonimo[s.texto]} onClick={() => anadirComoSinonimo(s.texto)}>
                              Añadir
                            </Button>
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </SectionCard>
              )}
            </div>
          </div>
        </>
      )}

      {/* Catálogo de orígenes, plegado */}
      <div className="cp-card mt-6" ref={editorRef}>
        <button
          type="button"
          onClick={() => setEditorAbierto((v) => !v)}
          aria-expanded={editorAbierto}
          className="w-full flex items-center gap-2 p-4 text-left text-sm font-semibold text-[#13231E]"
          data-testid="captacion-catalogo-toggle"
        >
          {editorAbierto ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          Catálogo de orígenes
          {catalogo && (
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              {catalogo.por_defecto
                ? 'valores por defecto'
                : `editado${catalogo.actualizado_por ? ` por ${catalogo.actualizado_por}` : ''}`}
            </span>
          )}
        </button>
        {editorAbierto && (
          <div className="px-4 pb-4" data-testid="captacion-catalogo">
            {falloCatalogo ? (
              <p className="text-sm text-red-700 dark:text-red-300" role="alert">
                No se ha podido leer el catálogo: {falloCatalogo}
              </p>
            ) : (
              <>
                {catalogo?.aviso && (
                  <p className="text-sm text-amber-800 dark:text-amber-300 mb-3" role="alert" data-testid="captacion-catalogo-aviso-lectura">
                    {catalogo.aviso}
                  </p>
                )}
                <p className="text-xs text-muted-foreground mb-3">
                  Cada origen agrupa lo que se escribe en «cómo nos conoció»: un sinónimo por línea, sin
                  importar acentos ni mayúsculas. Lo que no casa con ninguno va a «Otros», que no se puede
                  quitar. Si cambias el nombre de un origen, el nombre viejo se queda como sinónimo.
                  «No lo recuerda» se cuenta aparte: sus sinónimos se editan, su nombre no. «Sin origen»
                  (ficha y LOPD vacías) no es editable.
                </p>
                <div className="space-y-2">
                  {borrador.map((o) => {
                    // La fila de «Otros» es la que vino así del servidor: la
                    // que se escriba «Otros» en una fila nueva sigue editable.
                    const esOtros = o.original === nombreOtros;
                    const renombrado = !!o.original && !!normalizar(o.origen)
                      && normalizar(o.origen) !== normalizar(o.original);
                    const error = erroresNombre[o.clave];
                    return (
                      <div key={o.clave} className="grid grid-cols-1 sm:grid-cols-[180px_1fr_auto] gap-2 items-start"
                        data-testid={`captacion-catalogo-${slug(o.original || o.origen) || o.clave}`}>
                        <div>
                          <input
                            value={o.origen}
                            onChange={(e) => cambiarOrigen(o.clave, 'origen', e.target.value)}
                            disabled={esOtros}
                            aria-label="Nombre del origen"
                            aria-invalid={!!error}
                            className={`w-full px-2 py-1 rounded-md border bg-card text-sm disabled:opacity-70 ${
                              error ? 'border-red-400' : 'border-border'}`}
                          />
                          {error && (
                            <p className="mt-1 text-[11px] text-red-700 dark:text-red-300" data-testid="captacion-catalogo-error-nombre">
                              {error}
                            </p>
                          )}
                          {renombrado && !error && (
                            <p className="mt-1 text-[11px] text-muted-foreground" data-testid="captacion-catalogo-renombrado">
                              {avisoRenombrado(o)}
                            </p>
                          )}
                          {renombrado && (
                            <button type="button" className="mt-0.5 text-[11px] underline text-[#2E6E5E]"
                              data-testid="captacion-catalogo-deshacer"
                              onClick={() => cambiarOrigen(o.clave, 'origen', o.original)}>
                              Deshacer (volver a «{o.original}»)
                            </button>
                          )}
                        </div>
                        <textarea
                          value={o.sinonimos}
                          onChange={(e) => cambiarOrigen(o.clave, 'sinonimos', e.target.value)}
                          aria-label={`Sinónimos de ${o.origen}`}
                          placeholder="un sinónimo por línea"
                          rows={Math.min(Math.max(o.sinonimos.split('\n').length, 2), 6)}
                          className="px-2 py-1 rounded-md border border-border bg-card text-sm"
                        />
                        <Button type="button" variant="ghost" size="sm" disabled={esOtros}
                          aria-label={`Quitar ${o.origen}`}
                          onClick={() => pedirQuitar(o)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        {quitando === o.clave && (
                          <div className="sm:col-span-3 rounded-md border border-amber-300 bg-amber-50/60 dark:bg-amber-950/30 p-3 text-sm"
                            data-testid="captacion-catalogo-quitar">
                            <p className="mb-2">
                              «{o.original}» tiene {fichasTexto(fichasDe(o.original))}. ¿A qué origen pasan? Su
                              nombre y sus sinónimos se guardarán allí.
                            </p>
                            <div className="flex flex-wrap items-center gap-2">
                              <select value={destinoQuitar} onChange={(e) => setDestinoQuitar(e.target.value)}
                                aria-label={`Origen al que pasan las fichas de ${o.original}`}
                                className="px-2 py-1 rounded-md border border-border bg-card text-sm">
                                <option value="">Elige un origen…</option>
                                {borrador.filter((x) => x.clave !== o.clave && x.origen.trim()).map((x) => (
                                  <option key={x.clave} value={x.clave}>{x.origen.trim()}</option>
                                ))}
                              </select>
                              <Button type="button" size="sm" variant="outline" disabled={!destinoQuitar}
                                data-testid="captacion-catalogo-quitar-confirmar" onClick={confirmarQuitar}>
                                Quitar y pasar sus fichas
                              </Button>
                              <Button type="button" size="sm" variant="ghost" onClick={() => setQuitando(null)}>
                                Cancelar
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {/* La fila reservada: el nombre no se toca (lo guarda así el
                      desplegable de la ficha y de la LOPD) ni se quita. */}
                  <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr_auto] gap-2 items-start pt-2 border-t border-border"
                    data-testid="captacion-catalogo-no-lo-recuerda">
                    <input
                      value={catalogo?.no_lo_recuerda || 'No lo recuerda'}
                      disabled
                      readOnly
                      aria-label="Nombre reservado"
                      className="px-2 py-1 rounded-md border border-border bg-card text-sm disabled:opacity-70"
                    />
                    <textarea
                      value={noLoRecuerda}
                      onChange={(e) => setNoLoRecuerda(e.target.value)}
                      aria-label="Sinónimos de No lo recuerda"
                      placeholder="un sinónimo por línea"
                      rows={Math.min(Math.max(noLoRecuerda.split('\n').length, 2), 6)}
                      className="px-2 py-1 rounded-md border border-border bg-card text-sm"
                    />
                    <span className="hidden sm:block w-9" />
                  </div>
                </div>
                {pasos.length > 0 && (
                  <ul className="mt-3 text-xs text-muted-foreground space-y-0.5" data-testid="captacion-catalogo-pasos">
                    {pasos.map((p) => (
                      <li key={p.origen}>
                        {nombreDeClave(p.destinoClave)
                          ? `«${p.origen}» (${fichasTexto(p.fichas)}) pasará a «${nombreDeClave(p.destinoClave)}» al guardar.`
                          : <span className="text-red-700 dark:text-red-300">{`«${p.origen}» (${fichasTexto(p.fichas)}): su destino ya no está en el catálogo; elige otro destino (vuelve a añadirlo y quítalo de nuevo).`}</span>}
                      </li>
                    ))}
                  </ul>
                )}
                {restaurando && (
                  <div className="mt-3 rounded-md border border-amber-300 bg-amber-50/60 dark:bg-amber-950/30 p-3 text-sm"
                    role="alert" data-testid="captacion-restaurar-aviso">
                    <p className="mb-1">Al volver a los valores de fábrica se quitan estos orígenes propios:</p>
                    <ul className="list-disc pl-5 mb-2">
                      {restaurando.map((p) => <li key={p.nombre}>«{p.nombre}» ({fichasTexto(p.fichas)})</li>)}
                    </ul>
                    <p className="mb-2 text-xs text-muted-foreground">
                      Sus nombres se guardarán como sinónimos de «{nombreOtros}», para que sus fichas no se queden sin
                      clasificar. Los sinónimos que hayas añadido a los de fábrica se pierden.
                    </p>
                    <div className="flex gap-2">
                      <Button type="button" size="sm" variant="outline" data-testid="captacion-restaurar-confirmar"
                        onClick={() => aplicarRestaurar(restaurando)}>
                        Restaurar
                      </Button>
                      <Button type="button" size="sm" variant="ghost" onClick={() => setRestaurando(null)}>
                        Cancelar
                      </Button>
                    </div>
                  </div>
                )}
                {avisosGuardado.length > 0 && (
                  <ul className="mt-3 text-sm text-amber-800 dark:text-amber-300 space-y-0.5" role="status"
                    data-testid="captacion-catalogo-avisos">
                    {avisosGuardado.map((a) => <li key={a}>{a}</li>)}
                  </ul>
                )}
                {errorGuardar && (
                  <p className="text-sm text-red-700 dark:text-red-300 mt-3" role="alert" data-testid="captacion-catalogo-error">
                    {errorGuardar}
                  </p>
                )}
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button type="button" variant="outline" size="sm"
                    onClick={() => setBorrador((b) => [...b.slice(0, -1), ...aBorrador([{ origen: '', sinonimos: [] }]), ...b.slice(-1)])}>
                    <Plus className="w-4 h-4 mr-1.5" /> Añadir origen
                  </Button>
                  <Button type="button" variant="outline" size="sm"
                    onClick={pedirRestaurar}>
                    Volver a los valores por defecto
                  </Button>
                  <Button type="button" size="sm" className="bg-[#2E6E5E] hover:bg-[#1E4A3F] ml-auto"
                    disabled={guardando || hayErrores} onClick={guardarCatalogo} data-testid="captacion-catalogo-guardar">
                    {guardando && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Guardar catálogo
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
